<template>
  <div class="rental-place-instruments">
    <div class="panel-header">
      <h3>Instruments</h3>
      <span class="count">{{ instruments.length }}</span>
    </div>

    <form class="add-row" @submit.prevent="addInstrument">
      <Multiselect
        v-model="selectedInstrument"
        :options="availableInstruments"
        :custom-label="instrumentOptionLabel"
        track-by="id"
        placeholder="Search instruments to add…"
        :show-labels="false"
        class="instrument-select"
      >
        <template #noResult>No instruments match your search.</template>
        <template #noOptions>All instruments are already added.</template>
      </Multiselect>
      <button type="submit" class="add-button" :disabled="!selectedInstrument || isSaving">
        <i class="bi bi-plus-lg"></i> Add
      </button>
    </form>

    <input
      v-model="searchQuery"
      type="text"
      class="search-input"
      placeholder="Search by name, ID or location..."
      aria-label="Search instruments"
    />

    <table class="instrument-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>ID</th>
          <th>Location</th>
          <th class="actions-col"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="instrument in paginatedInstruments"
          :key="instrument.id"
          class="instrument-row"
          @click="openInstrument(instrument.id)"
        >
          <td>{{ instrument.name }}</td>
          <td>{{ instrument.serial || '—' }}</td>
          <td>{{ locationNames[instrument.location_id] || '—' }}</td>
          <td class="actions-col">
            <button
              type="button"
              class="remove-btn"
              title="Remove from support expense"
              :disabled="isSaving"
              @click.stop="removeInstrument(instrument)"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </td>
        </tr>
        <tr v-if="!isLoading && !instruments.length">
          <td colspan="4" class="empty-row">No instruments in this support expense yet.</td>
        </tr>
        <tr v-else-if="!isLoading && !filteredInstruments.length">
          <td colspan="4" class="empty-row">No instruments match your search.</td>
        </tr>
      </tbody>
    </table>

    <div v-if="totalPages > 1" class="pagination">
      <button type="button" :disabled="currentPage === 1" @click="currentPage = 1">First</button>
      <button
        v-for="page in visiblePages"
        :key="page"
        type="button"
        :class="{ active: page === currentPage }"
        @click="currentPage = page"
      >
        {{ page }}
      </button>
      <button type="button" :disabled="currentPage === totalPages" @click="currentPage = totalPages">Last</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import { useToast } from 'vue-toastification';
import apiService from '@/services/apiService';
import { API_ENDPOINTS } from '@/services/config';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';

const PAGE_SIZE = 10;
const MAX_PAGE_BUTTONS = 10;

export default {
  name: 'RentalPlaceInstruments',
  components: { Multiselect },
  props: {
    rentalPlaceId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      instruments: [],
      allInstruments: [],
      locations: [],
      selectedInstrument: null,
      searchQuery: '',
      currentPage: 1,
      isLoading: false,
      isSaving: false,
    };
  },
  computed: {
    ...mapState(['loggedInUser']),
    locationNames() {
      return Object.fromEntries(this.locations.map((location) => [location.id, location.name]));
    },
    availableInstruments() {
      const assignedIds = new Set(this.instruments.map((instrument) => instrument.id));
      return this.allInstruments
        .filter((instrument) => !assignedIds.has(instrument.id))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    filteredInstruments() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return this.instruments;

      return this.instruments.filter((instrument) =>
        [instrument.name, instrument.serial, this.locationNames[instrument.location_id]]
          .some((value) => String(value || '').toLowerCase().includes(query)));
    },
    totalPages() {
      return Math.ceil(this.filteredInstruments.length / PAGE_SIZE);
    },
    paginatedInstruments() {
      const start = (this.currentPage - 1) * PAGE_SIZE;
      return this.filteredInstruments.slice(start, start + PAGE_SIZE);
    },
    visiblePages() {
      const count = Math.min(MAX_PAGE_BUTTONS, this.totalPages);
      const start = Math.min(
        Math.max(1, this.currentPage - Math.floor(count / 2)),
        this.totalPages - count + 1
      );
      return Array.from({ length: count }, (_, index) => start + index);
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    totalPages(pages) {
      if (this.currentPage > pages) this.currentPage = Math.max(pages, 1);
    },
  },
  created() {
    this.fetchInstruments();
    this.fetchAllInstruments();
    this.fetchLocations();
  },
  methods: {
    async fetchInstruments() {
      this.isLoading = true;
      try {
        const response = await axios.get(API_ENDPOINTS.rentalPlaceInstruments(this.rentalPlaceId));
        this.instruments = response.data;
      } catch (error) {
        this.toast.error('Failed to fetch instruments');
        console.error('Failed to fetch rental place instruments:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchAllInstruments() {
      try {
        const response = await axios.get(API_ENDPOINTS.allInstruments);
        this.allInstruments = response.data;
      } catch (error) {
        console.error('Failed to fetch instruments:', error);
      }
    },
    async fetchLocations() {
      try {
        const response = await axios.get(API_ENDPOINTS.locations);
        this.locations = response.data;
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    },
    async addInstrument() {
      const instrument = this.selectedInstrument;
      if (!instrument) return;

      if (instrument.rental_place_id && !window.confirm(
        `"${instrument.name}" already belongs to another support expense. Move it here?`
      )) {
        return;
      }

      await this.assign(instrument, Number(this.rentalPlaceId), 'Instrument added');
      this.selectedInstrument = null;
    },
    removeInstrument(instrument) {
      return this.assign(instrument, null, 'Instrument removed');
    },
    async assign(instrument, rentalPlaceId, successMessage) {
      this.isSaving = true;
      try {
        await apiService.setInstrumentRentalPlace(instrument, rentalPlaceId, this.loggedInUser?.entra_id);
        this.toast.success(successMessage);
        await Promise.all([this.fetchInstruments(), this.fetchAllInstruments()]);
      } catch (error) {
        this.toast.error(error?.response?.data?.message || 'Failed to update instrument');
        console.error('Failed to update instrument rental place:', error);
      } finally {
        this.isSaving = false;
      }
    },
    instrumentOptionLabel(instrument) {
      return instrument.serial ? `${instrument.name} (${instrument.serial})` : instrument.name;
    },
    openInstrument(id) {
      this.$router.push({ name: 'InstrumentDetailPage', params: { id } });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.rental-place-instruments {
  padding: 15px 20px;
  border-radius: 8px;
  background-color: $secondary-color;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  .panel-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;

    h3 {
      margin: 0;
      color: $primary-color;
    }

    .count {
      padding: 2px 10px;
      border-radius: 999px;
      background: rgba($primary-color, 0.1);
      color: $primary-color;
      font-weight: 600;
      font-size: 0.85rem;
    }
  }

  .add-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 12px;

    .instrument-select {
      flex: 1;
      min-width: 0;
    }
  }

  .add-button {
    width: auto;
    min-height: 40px;
    margin: 0;
    padding: 8px 16px;
    background-color: $primary-color;
    color: #fff;
    border: none;
    border-radius: 5px;
    white-space: nowrap;
    cursor: pointer;

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  .instrument-table {
    width: 100%;

    th {
      background-color: $form-color;
      border: 1px solid $primary-color;
    }
  }

  .instrument-row {
    cursor: pointer;

    &:hover {
      background-color: $form-color;
    }
  }

  .actions-col {
    width: 44px;
    text-align: center;
  }

  .empty-row {
    text-align: center;
    color: rgba($primary-color, 0.7);
    font-style: italic;
  }

  .search-input {
    width: 100%;
    margin: 0 0 8px;
  }

  .pagination {
    display: flex;
    justify-content: center;
    flex-wrap: nowrap;
    gap: 4px;
    margin-top: 12px;
    overflow-x: auto;

    button {
      flex: 0 0 auto;
      width: auto;
      margin: 0;
      padding: 4px 9px;
      background-color: $primary-color;
      border: 1px solid $secondary-color;
      border-radius: 5px;
      white-space: nowrap;
      cursor: pointer;

      &:hover:not(:disabled),
      &.active {
        background-color: darken($primary-color, 10%);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}
</style>
